import type * as Phaser from 'phaser';
import { clamp } from '../sim/math.ts';
import {
  PINCH_MIN_SPAN_PX,
  pinchCenter,
  pinchPair,
  pinchSpan,
  pinchZoom,
} from './pinch.ts';
import {
  WHEEL_ZOOM_IN,
  WHEEL_ZOOM_OUT,
  constrainScroll,
  scrollToKeepWorldPoint,
  type ConstrainFit,
} from './view.ts';

export type CameraControlOptions = {
  minZoom: number;
  maxZoom: number;
  constrainFit: ConstrainFit;
  /** Play: Phaser setBounds while the map is larger than the view. */
  usePhaserBoundsWhenMapFillsView?: boolean;
};

/** Next zoom after one wheel notch. */
export function steppedZoom(
  currentZoom: number,
  zoomOut: boolean,
  minZoom: number,
  maxZoom: number,
): number {
  return clamp(
    currentZoom * (zoomOut ? WHEEL_ZOOM_OUT : WHEEL_ZOOM_IN),
    minZoom,
    maxZoom,
  );
}

/**
 * Shared pinch, pan, wheel, and constrain for play and the editor.
 * Scene-specific policy (follow, auto zoom, minimap) stays in the scene.
 */
export class CameraControl {
  panning = false;
  pinching = false;
  pinchConsumed = false;

  private worldWidth = 0;
  private worldHeight = 0;
  private pinchStartSpan = 0;
  private pinchStartZoom = 1;
  private panStartScrollX = 0;
  private panStartScrollY = 0;
  private panStartX = 0;
  private panStartY = 0;
  private readonly options: CameraControlOptions;

  constructor(options: CameraControlOptions) {
    this.options = options;
  }

  setWorldBounds(bounds: { width: number; height: number }): void {
    this.worldWidth = bounds.width;
    this.worldHeight = bounds.height;
  }

  reset(): void {
    this.panning = false;
    this.pinching = false;
    this.pinchConsumed = false;
    this.pinchStartSpan = 0;
    this.pinchStartZoom = 1;
  }

  /** Record the pointer so a later drag can become a pan. */
  armPan(cam: Phaser.Cameras.Scene2D.Camera, x: number, y: number): void {
    this.panning = false;
    this.panStartScrollX = cam.scrollX;
    this.panStartScrollY = cam.scrollY;
    this.panStartX = x;
    this.panStartY = y;
  }

  beginPan(cam: Phaser.Cameras.Scene2D.Camera, x: number, y: number): void {
    this.armPan(cam, x, y);
    this.panning = true;
  }

  applyPan(cam: Phaser.Cameras.Scene2D.Camera, x: number, y: number): void {
    cam.scrollX = this.panStartScrollX - (x - this.panStartX) / cam.zoom;
    cam.scrollY = this.panStartScrollY - (y - this.panStartY) / cam.zoom;
    this.constrain(cam);
  }

  endPan(): void {
    this.panning = false;
  }

  pinchPointers(
    pointers: readonly Phaser.Input.Pointer[],
  ): [Phaser.Input.Pointer, Phaser.Input.Pointer] | null {
    return pinchPair(pointers);
  }

  /**
   * Starts a pinch, or keeps one already in progress.
   * `beforeCapture` runs after the pair is valid and before `cam.zoom` is stored — play uses
   * it to cancel a plant so rest-zoom is not the pinch origin.
   */
  beginPinch(
    cam: Phaser.Cameras.Scene2D.Camera,
    pointers: readonly Phaser.Input.Pointer[],
    beforeCapture?: () => void,
  ): boolean {
    if (this.pinching) {
      return true;
    }

    const pair = this.pinchPointers(pointers);

    if (!pair) {
      return false;
    }

    const span = pinchSpan(pair[0], pair[1]);

    if (span < PINCH_MIN_SPAN_PX) {
      return false;
    }

    beforeCapture?.();

    this.panning = false;
    this.pinching = true;
    this.pinchConsumed = true;
    this.pinchStartSpan = span;
    this.pinchStartZoom = cam.zoom;

    return true;
  }

  applyPinch(
    pointers: readonly Phaser.Input.Pointer[],
  ): { zoom: number; center: { x: number; y: number } } | null {
    const pair = this.pinchPointers(pointers);

    if (!pair) {
      return null;
    }

    return {
      zoom: pinchZoom(
        this.pinchStartZoom,
        this.pinchStartSpan,
        pinchSpan(pair[0], pair[1]),
        this.options.minZoom,
        this.options.maxZoom,
      ),
      center: pinchCenter(pair[0], pair[1]),
    };
  }

  releasePinch(pointers: readonly Phaser.Input.Pointer[]): void {
    if (this.pinching && !this.pinchPointers(pointers)) {
      this.pinching = false;
    }

    if (this.pinchConsumed && !pointers.some((pointer) => pointer.isDown)) {
      this.pinchConsumed = false;
    }
  }

  wheelZoom(currentZoom: number, zoomOut: boolean): number {
    return steppedZoom(
      currentZoom,
      zoomOut,
      this.options.minZoom,
      this.options.maxZoom,
    );
  }

  /**
   * Sets zoom and keeps `anchor` on the same world point.
   * Returns false when the zoom did not change.
   */
  applyZoom(
    cam: Phaser.Cameras.Scene2D.Camera,
    nextZoom: number,
    anchorX: number,
    anchorY: number,
  ): boolean {
    if (nextZoom === cam.zoom) {
      return false;
    }

    const oldZoom = cam.zoom;
    cam.setZoom(nextZoom);
    cam.scrollX = scrollToKeepWorldPoint(
      cam.scrollX,
      cam.width,
      oldZoom,
      nextZoom,
      anchorX,
    );
    cam.scrollY = scrollToKeepWorldPoint(
      cam.scrollY,
      cam.height,
      oldZoom,
      nextZoom,
      anchorY,
    );
    this.constrain(cam);

    return true;
  }

  constrain(cam: Phaser.Cameras.Scene2D.Camera): void {
    const width = this.worldWidth;
    const height = this.worldHeight;
    const fit = this.options.constrainFit;

    if (
      this.options.usePhaserBoundsWhenMapFillsView &&
      cam.width / cam.zoom < width &&
      cam.height / cam.zoom < height
    ) {
      cam.setBounds(0, 0, width, height);
      return;
    }

    if (this.options.usePhaserBoundsWhenMapFillsView) {
      cam.removeBounds();
    }

    cam.setScroll(
      constrainScroll(cam.scrollX, cam.width, cam.zoom, width, { fit }),
      constrainScroll(cam.scrollY, cam.height, cam.zoom, height, { fit }),
    );
  }
}
